import { TASK_TRANSITIONS, type Task, type TaskStatus } from "@agentsaas/protocol";
import type { Tone } from "@agentsaas/ui";

/** 看板列（A2A 9 态归并成 5 列） */
export type BoardColumn = "todo" | "working" | "blocked" | "done" | "closed";
export const BOARD_COLUMNS: { id: BoardColumn; statuses: TaskStatus[] }[] = [
  { id: "todo", statuses: ["submitted", "unknown"] },
  { id: "working", statuses: ["working"] },
  { id: "blocked", statuses: ["input_required", "auth_required"] },
  { id: "done", statuses: ["completed"] },
  { id: "closed", statuses: ["failed", "canceled", "rejected"] },
];

export function columnOf(s: TaskStatus): BoardColumn {
  return BOARD_COLUMNS.find((c) => c.statuses.includes(s))!.id;
}

export const TASK_TONE: Record<TaskStatus, Tone> = {
  submitted: "neutral",
  unknown: "neutral",
  working: "accent",
  input_required: "warning",
  auth_required: "warning",
  completed: "success",
  failed: "danger",
  canceled: "neutral",
  rejected: "danger",
};

/** 按创建时间排序（新的在后） */
const byCreated = (a: Task, b: Task) => a.createdAt.localeCompare(b.createdAt);

export function groupByColumn(tasks: Task[]): Record<BoardColumn, Task[]> {
  const out = Object.fromEntries(BOARD_COLUMNS.map((c) => [c.id, [] as Task[]])) as Record<BoardColumn, Task[]>;
  for (const t of [...tasks].sort(byCreated)) out[columnOf(t.status)].push(t);
  return out;
}

export interface TreeNode {
  task: Task;
  children: TreeNode[];
}

/** 任务树：parentId 找不到的任务作为根 */
export function buildTree(tasks: Task[]): TreeNode[] {
  const ids = new Set(tasks.map((t) => t.id));
  const kids = new Map<string, Task[]>();
  const roots: Task[] = [];
  for (const t of [...tasks].sort(byCreated)) {
    if (t.parentId && ids.has(t.parentId) && t.parentId !== t.id) {
      const list = kids.get(t.parentId) ?? [];
      list.push(t);
      kids.set(t.parentId, list);
    } else roots.push(t);
  }
  const seen = new Set<string>();
  const node = (t: Task): TreeNode => {
    seen.add(t.id);
    return { task: t, children: (kids.get(t.id) ?? []).filter((c) => !seen.has(c.id)).map(node) };
  };
  return roots.map(node);
}

/** 子树的完成进度（不含自身） */
export function progressOf(n: TreeNode): { done: number; total: number } {
  let done = 0;
  let total = 0;
  const walk = (x: TreeNode) => {
    for (const c of x.children) {
      total++;
      if (c.task.status === "completed") done++;
      walk(c);
    }
  };
  walk(n);
  return { done, total };
}

/** 人能对任务做的操作（与 mock / bus 的状态机一致） */
export function taskActions(t: Task) {
  const open = TASK_TRANSITIONS[t.status].length > 0;
  return {
    assign: open,
    confirm: t.status === "completed" && !t.confirmedAt,
    rework: ["completed", "failed", "rejected", "canceled"].includes(t.status),
    cancel: TASK_TRANSITIONS[t.status].includes("canceled"),
  };
}

export interface HistoryEntry {
  status: TaskStatus | "confirmed";
  at: string;
}

/**
 * 状态历史。契约里 Task 没有历史字段（已提给集成者），这里合并：
 * 创建时间（submitted）+ 本页面会话里观察到的状态变更（实时事件）+ 当前状态（updatedAt）+ 验收时间。
 */
export function mergeHistory(t: Task, observed: HistoryEntry[] = []): HistoryEntry[] {
  const all: HistoryEntry[] = [{ status: "submitted", at: t.createdAt }, ...observed];
  if (!all.some((h) => h.status === t.status)) all.push({ status: t.status, at: t.updatedAt });
  if (t.confirmedAt) all.push({ status: "confirmed", at: t.confirmedAt });
  const seen = new Set<string>();
  return all
    .sort((a, b) => a.at.localeCompare(b.at))
    .filter((h) => {
      const k = `${h.status}@${h.at}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
}
